export type ContextKey = "history" | "timing" | "channel" | "rule";
export type DraftPart = { text: string; source?: ContextKey };

type WalkthroughExample = {
  id: string;
  label: string;
  name: string;
  initials: string;
  role: string;
  context: { id: ContextKey; value: string; source: string }[];
  rule: string;
  evidence: string;
  nextStep: string;
  draft: DraftPart[];
  conversation: {
    date: string;
    messages: { text: string; time: string; direction: "incoming" | "outgoing" }[];
  };
};

export const walkthroughExamples: WalkthroughExample[] = [
  {
    id: "dental", label: "Dental clinics", name: "Priscilla Yeo", initials: "PY", role: "Dental patient",
    context: [
      { id: "history", value: "Last visit: routine check-up", source: "Visit history · 4 completed visits" },
      { id: "timing", value: "Prefers weekday mornings before work", source: "Past conversations" },
      { id: "channel", value: "Responds on WhatsApp", source: "Message history" },
    ],
    rule: "If the last visit was 5+ months ago, invite to rebook",
    evidence: "Priscilla’s last visit was 5 months ago",
    nextStep: "Invite Priscilla to book her next check-up",
    draft: [
      { text: "Hi Priscilla, hope you’ve been well! It’s been five months since " },
      { text: "your last check-up", source: "history" },
      { text: ". I remember " },
      { text: "weekday mornings", source: "timing" },
      { text: " are easiest before work. Shall I send you a couple of times for your " },
      { text: "next check-up", source: "rule" }, { text: "? Happy to sort the booking out here." },
    ],
    conversation: {
      date: "5 months ago",
      messages: [
        { direction: "outgoing", text: "All set for Thursday at 9, Priscilla. See you then!", time: "14:18" },
        { direction: "incoming", text: "Perfect, mornings before work are easiest for me. Thank you!", time: "14:20" },
        { direction: "outgoing", text: "Noted. We’ll keep that in mind for next time.", time: "14:21" },
      ],
    },
  },
  {
    id: "gym", label: "Gyms", name: "Marcus Tan", initials: "MT", role: "Gym member",
    context: [
      { id: "history", value: "8 strength classes completed", source: "Class attendance" },
      { id: "timing", value: "Prefers after-work sessions", source: "Past class bookings" },
      { id: "channel", value: "Responds on WhatsApp", source: "Message history" },
    ],
    rule: "If there is no class booking for 2 weeks, offer a check-in",
    evidence: "Marcus hasn’t booked a class in 2 weeks",
    nextStep: "Help Marcus find his next strength class",
    draft: [
      { text: "Hi Marcus, how’s your week going? You’d built such a good rhythm with those " }, { text: "eight strength classes", source: "history" },
      { text: ". If life’s been busy, no worries. Fancy finding your " }, { text: "next session", source: "rule" },
      { text: "? I remember an " }, { text: "after-work slot", source: "timing" }, { text: " suits you best, so I can send a couple of options." },
    ],
    conversation: {
      date: "2 weeks ago",
      messages: [
        { direction: "outgoing", text: "That’s eight strength classes, Marcus. Great effort today!", time: "18:46" },
        { direction: "incoming", text: "Thanks! After work is definitely the best time for me.", time: "18:50" },
        { direction: "outgoing", text: "Got it. We’ll look for evening sessions when you’re ready.", time: "18:51" },
      ],
    },
  },
  {
    id: "finance", label: "Financial advisors", name: "Joanne Lim", initials: "JL", role: "Advisory client",
    context: [
      { id: "history", value: "Annual review completed last March", source: "Meeting notes" },
      { id: "timing", value: "Prefers Friday afternoons", source: "Scheduling preferences" },
      { id: "channel", value: "Responds on WhatsApp", source: "Message history" },
    ],
    rule: "If the last review was 12 months ago, offer an annual review",
    evidence: "It has been 12 months since Joanne’s last review",
    nextStep: "Invite Joanne to schedule her annual review",
    draft: [
      { text: "Hi Joanne, hope you’ve been keeping well. It’s been a year since our " }, { text: "review in March", source: "history" },
      { text: ", so I thought we could set aside a little time to revisit your plans and anything that’s changed. Would a " }, { text: "Friday afternoon", source: "timing" },
      { text: " suit you for your " }, { text: "annual review", source: "rule" }, { text: "? I can send a couple of options here." },
    ],
    conversation: {
      date: "Last March",
      messages: [
        { direction: "outgoing", text: "Thanks for making time for your review today, Joanne.", time: "15:32" },
        { direction: "incoming", text: "Thank you, that was helpful. Fridays are easiest for me.", time: "15:36" },
        { direction: "outgoing", text: "I’ll keep Friday afternoons in mind for our next catch-up.", time: "15:38" },
      ],
    },
  },
];
